import React, { useState, useEffect, useRef } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Switch,
	MenuItem,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
	deletePromoClient,
	getPromoClientById,
	postPromoClient,
	putPromoClient,
} from "../../../../api/promo-client";
import { getPromoClientSectorsAdmin } from "../../../../api/promo-client-sector";
import { uploadSingleFile } from "../../../../api/files";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { SERVER_URL } from "../../../../utils";

const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function resolveLogoUrl(raw) {
	const p = String(raw || "").trim();
	if (!p) return "";
	if (/^https?:\/\//i.test(p) || p.startsWith("data:")) return p;
	if (p.startsWith("/")) return `${String(SERVER_URL || "").replace(/\/$/, "")}${p}`;
	return `${String(SERVER_URL || "").replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
}

function getEmptyClient() {
	return {
		name: "",
		logoUrl: "",
		sectorId: "",
		sortOrder: 0,
		active: true,
		showOnHome: true,
	};
}

export default function EditPromoClientPage() {
	const clientId = useParams().id;
	const history = useHistory();
	const fileInputRef = useRef(null);
	const [client, setClient] = useState(getEmptyClient());
	const [sectors, setSectors] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		let mounted = true;
		getPromoClientSectorsAdmin()
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) setSectors(res.data || []);
			})
			.catch(() => {});
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		let mounted = true;
		if (!clientId) {
			disableLoadingData();
			return () => {
				mounted = false;
			};
		}
		getPromoClientById(clientId)
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) {
					const c = res.data || {};
					setClient({
						name: c.name || "",
						logoUrl: c.logoUrl || "",
						sectorId: c.sectorId?._id || c.sectorId || "",
						sortOrder: c.sortOrder ?? 0,
						active: c.active !== false,
						showOnHome: c.showOnHome !== false,
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudo obtener el cliente." });
				history.push("/promo-clients");
			});
		return () => {
			mounted = false;
		};
	}, [clientId, disableLoadingData, history]);

	async function handleLogoUpload(e) {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		setUploading(true);
		try {
			const res = await uploadSingleFile(file, "promo-clients");
			if (res?.data?.fileURL) {
				setClient((prev) => ({ ...prev, logoUrl: res.data.fileURL }));
				alertSuccess({ title: "Logo subido", customMessage: "Imagen cargada correctamente." });
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo subir el logo." });
		} finally {
			setUploading(false);
		}
	}

	function saveClient() {
		const body = {
			name: String(client.name || "").trim(),
			logoUrl: String(client.logoUrl || "").trim(),
			sectorId: client.sectorId,
			active: !!client.active,
			showOnHome: !!client.showOnHome,
		};

		if (!body.name) {
			alertError({ customMessage: "El nombre es obligatorio." });
			return;
		}
		if (!body.sectorId) {
			alertError({ customMessage: "Selecciona un sector." });
			return;
		}

		if (!clientId) {
			postPromoClient(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({ title: "Guardado", customMessage: "Cliente creado correctamente." });
						history.push("/promo-clients");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudo crear el cliente." });
				});
		} else {
			putPromoClient(clientId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({ title: "Guardado", customMessage: "Cambios guardados correctamente." });
						history.push("/promo-clients");
					}
				})
				.catch((error) => {
					alertError({ error, customMessage: "No se pudieron guardar los cambios." });
				});
		}
	}

	const handleChange = (field) => (event) => {
		setClient({ ...client, [field]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;

	const logoPreview = resolveLogoUrl(client.logoUrl);

	return (
		<>
			<Card>
				<CardHeader title={clientId ? "Editar cliente web" : "Nuevo cliente web"} />
				<CardBody>
					<TextField
						id="client-name"
						label="Nombre del cliente"
						value={client.name}
						onChange={handleChange("name")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						required
					/>
					<TextField
						id="client-sector"
						select
						label="Sector"
						helperText="Agrupa el cliente en la página /clientes de evenely.com"
						value={client.sectorId}
						onChange={handleChange("sectorId")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
						required
					>
						<MenuItem value="">
							<em>Selecciona un sector</em>
						</MenuItem>
						{sectors.map((s) => (
							<MenuItem key={s._id} value={s._id}>
								{s.name}
							</MenuItem>
						))}
					</TextField>

					{logoPreview ? (
						<div
							style={{
								marginTop: 16,
								marginBottom: 8,
								padding: 16,
								border: "1px solid #ebedf3",
								borderRadius: 4,
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: 180,
								minHeight: 88,
								background: "#f9fafb",
							}}
						>
							<img
								src={logoPreview}
								alt={client.name || "Logo"}
								style={{ maxHeight: 64, maxWidth: 220, objectFit: "contain" }}
							/>
						</div>
					) : null}
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*,.webp"
						style={{ display: "none" }}
						onChange={handleLogoUpload}
					/>
					<div style={{ marginTop: logoPreview ? 0 : 16 }}>
						<Button
							variant="outlined"
							color="primary"
							disabled={uploading}
							onClick={() => fileInputRef.current?.click()}
							style={{ marginRight: 12, marginBottom: 8 }}
						>
							{uploading ? "Subiendo…" : "Subir logo"}
						</Button>
					</div>
					<TextField
						id="client-logoUrl"
						label="URL del logo (opcional)"
						helperText="Puedes pegar una URL externa o usar el botón de subida"
						value={client.logoUrl}
						onChange={handleChange("logoUrl")}
						InputLabelProps={{ shrink: true }}
						margin="normal"
						variant="outlined"
						fullWidth
					/>
				</CardBody>
			</Card>

			<Card>
				<CardBody>
					<FormControlLabel
						control={
							<Switch
								checked={!!client.active}
								onChange={(e) => setClient({ ...client, active: e.target.checked })}
								color="primary"
							/>
						}
						label={client.active ? "Activo (visible en evenely.com)" : "Inactivo (oculto en la web)"}
					/>
					<div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
						Los clientes inactivos no aparecen en la home ni en la página de clientes.
					</div>
					<FormControlLabel
						control={
							<Switch
								checked={!!client.showOnHome}
								onChange={(e) => setClient({ ...client, showOnHome: e.target.checked })}
								color="primary"
								disabled={!client.active}
							/>
						}
						label={
							client.showOnHome
								? "Visible en la banda de patrocinadores de la home"
								: "No mostrar en la banda de la home"
						}
					/>
					<div style={{ fontSize: 12, color: "#888" }}>
						Sigue apareciendo en /clientes dentro de su sector si está activo. El orden se cambia desde el listado de clientes.
					</div>
				</CardBody>
			</Card>

			<Button
				onClick={() => history.push("/promo-clients")}
				variant="outlined"
				style={{ marginRight: 20 }}
			>
				Volver
			</Button>
			<Button onClick={saveClient} variant="outlined" color="primary" style={{ marginRight: 20 }}>
				Guardar cliente
			</Button>
			{clientId && (
				<>
					<MuiThemeProvider theme={theme}>
						<Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
							Eliminar cliente
						</Button>
					</MuiThemeProvider>

					<ConfirmDialog
						title="¿Seguro que quieres eliminar este cliente?"
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePromoClient(clientId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Cliente eliminado correctamente." });
										history.push("/promo-clients");
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar el cliente." });
								});
						}}
					/>
				</>
			)}
		</>
	);
}
